import {cn} from '../lib/utils'
import {Icon} from './Icon'
import {
  type ButtonHTMLAttributes,
  createContext,
  type HTMLAttributes,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

interface NumericRangeContextValue {
  value: number | undefined
  min: number | undefined
  max: number | undefined
  disabled: boolean
  readOnly: boolean
  holdToRepeat: boolean
  holdDelay: number
  holdInterval: number
  /** Moves the value one step. Returns `false` once the limit is reached. */
  stepBy: (direction: 1 | -1) => boolean
}

const NumericRangeContext = createContext<NumericRangeContextValue | null>(null)

function useNumericRangeContext(part: string): NumericRangeContextValue {
  const context = useContext(NumericRangeContext)
  if (!context) throw new Error(`<${part}> must be used within <NumericRange>`)
  return context
}

/** Rounds away binary floating point noise (`0.1 + 0.2`) to `precision` significant digits. */
function roundToPrecision(n: number, precision: number | undefined): number {
  return Number(n.toPrecision(precision))
}

/** The next value in `direction` that is not in `skipValues`, or `undefined` once `limit` would be passed. */
function getNextValue(
  current: number,
  direction: 1 | -1,
  {
    step,
    precision,
    limit,
    skipValues,
  }: {step: number; precision?: number; limit?: number; skipValues: ReadonlySet<number>},
): number | undefined {
  let next = roundToPrecision(current + direction * step, precision)

  while (skipValues.has(next)) {
    next = roundToPrecision(next + direction * step, precision)
  }

  if (limit === undefined) return next
  return direction === 1 ? (next <= limit ? next : undefined) : next >= limit ? next : undefined
}

export interface NumericRangeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> {
  /** `NumericRangeDecrement`, `NumericRangeValue` and `NumericRangeIncrement`. Leave the buttons out for a read-only display. */
  children: ReactNode
  /** Current value. Pair it with `onValueChange`. */
  value: number | undefined
  /** Called with the next value each time a button steps it. */
  onValueChange: (value: number) => void
  /** Lower limit; the decrement button is disabled once it is reached. */
  min?: number
  /** Upper limit; the increment button is disabled once it is reached. */
  max?: number
  /** Amount added or removed per step. */
  step?: number
  /** Values the stepper jumps over. */
  skipValues?: number[]
  /** Significant digits kept when rounding after a step, so decimal steps do not drift. */
  precision?: number
  /** Marks the value as changed from its default: draws the selected border. */
  active?: boolean
  /** Shows the value without allowing changes. */
  readOnly?: boolean
  /** Dims the whole control and ignores presses. */
  disabled?: boolean
  /** Keep stepping while a button is held down. */
  holdToRepeat?: boolean
  /** Milliseconds a button has to be held before it starts repeating. */
  holdDelay?: number
  /** Milliseconds between repeated steps. */
  holdInterval?: number
}

/**
 * NumericRange
 *
 * Adjusts a number within a range with a decrement and an increment button either side of the value.
 * Compose it from `NumericRangeDecrement`, `NumericRangeValue` and `NumericRangeIncrement`; render only
 * `NumericRangeValue` for a read-only display, or put a placeholder in it while the field is unavailable.
 *
 * Controlled: the caller owns `value` and receives the next one through `onValueChange`. Buttons step by
 * `step`, skip `skipValues`, stop at `min` / `max`, and can repeat while held (`holdToRepeat`). They also
 * work from the keyboard (Enter / Space).
 *
 * Give each button an accessible name with `aria-label`.
 *
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=2932-99440&p=f&m=dev
 *
 * @example
 * <NumericRange max={10} min={0} value={quantity} onValueChange={setQuantity}>
 *   <NumericRangeDecrement aria-label="Decrease quantity" />
 *   <NumericRangeValue />
 *   <NumericRangeIncrement aria-label="Increase quantity" />
 * </NumericRange>
 */
export function NumericRange({
  children,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  skipValues,
  precision,
  active = false,
  readOnly = false,
  disabled = false,
  holdToRepeat = false,
  holdDelay = 500,
  holdInterval = 100,
  className,
  ...props
}: NumericRangeProps) {
  /* A held button steps faster than the parent re-renders, so the latest value is tracked here. */
  const valueRef = useRef(value ?? 0)
  useEffect(() => {
    valueRef.current = value ?? 0
  }, [value])

  const skipRef = useRef<ReadonlySet<number>>(new Set())
  useEffect(() => {
    skipRef.current = new Set(skipValues)
  }, [skipValues])

  const stepBy = useCallback(
    (direction: 1 | -1) => {
      const next = getNextValue(valueRef.current, direction, {
        step,
        precision,
        limit: direction === 1 ? max : min,
        skipValues: skipRef.current,
      })

      if (next === undefined) return false

      valueRef.current = next
      onValueChange(next)
      return true
    },
    [max, min, onValueChange, precision, step],
  )

  const context = useMemo<NumericRangeContextValue>(
    () => ({value, min, max, disabled, readOnly, holdToRepeat, holdDelay, holdInterval, stepBy}),
    [value, min, max, disabled, readOnly, holdToRepeat, holdDelay, holdInterval, stepBy],
  )

  return (
    <NumericRangeContext.Provider value={context}>
      <div
        {...props}
        role="group"
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        data-readonly={readOnly || undefined}
        className={cn(
          'ease-out box-border flex h-44 w-[164px] items-center overflow-hidden rounded-md border border-border bg-surface font-sans text-base leading-base font-light text-text transition-[border-color] duration-200',
          'has-[:focus-visible]:border-border-focus has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--color-border-focus)]',
          'data-[active]:border-border-selected',
          'data-[disabled]:border-border-disabled data-[disabled]:bg-surface-secondary data-[readonly]:bg-surface-secondary',
          className,
        )}
      >
        {children}
      </div>
    </NumericRangeContext.Provider>
  )
}

interface StepButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  /** `aria-label` of the button; icon-only controls need one. */
  'aria-label': string
}

const stepButtonClassName =
  'box-border flex size-44 shrink-0 touch-manipulation items-center justify-center bg-button-secondary p-10 text-text-button-secondary outline-none transition-[background-color] duration-200 not-disabled:cursor-pointer not-disabled:hover:bg-button-secondary-hover not-disabled:active:bg-button-secondary-pressed disabled:bg-button-secondary-disabled disabled:text-text-button-secondary-disabled'

function useStepButton(direction: 1 | -1) {
  const {value, min, max, disabled, readOnly, holdToRepeat, holdDelay, holdInterval, stepBy} =
    useNumericRangeContext(direction === 1 ? 'NumericRangeIncrement' : 'NumericRangeDecrement')
  const waitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const repeatTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const stop = useCallback(() => {
    clearTimeout(waitTimer.current)
    clearTimeout(repeatTimer.current)
    waitTimer.current = undefined
    repeatTimer.current = undefined
  }, [])

  useEffect(() => stop, [stop])

  const repeat = useCallback(() => {
    if (stepBy(direction)) {
      repeatTimer.current = setTimeout(repeat, holdInterval)
    }
  }, [direction, holdInterval, stepBy])

  const limit = direction === 1 ? max : min
  const isAtLimit =
    value !== undefined &&
    limit !== undefined &&
    (direction === 1 ? value >= limit : value <= limit)
  const isDisabled = disabled || readOnly || isAtLimit

  return {
    isDisabled,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return
        event.preventDefault()
        event.currentTarget.focus()

        if (holdToRepeat) {
          waitTimer.current = setTimeout(() => {
            waitTimer.current = undefined
            repeat()
          }, holdDelay)
        } else {
          stepBy(direction)
        }
      },
      onPointerUp: () => {
        /* Released before the hold delay: it was a plain press. */
        if (waitTimer.current !== undefined) {
          stop()
          stepBy(direction)
          return
        }
        stop()
      },
      onPointerLeave: stop,
      onPointerCancel: stop,
      /* Pointer presses are handled on pointerdown; a click without pointer detail comes from the keyboard. */
      onClick: (event: {detail: number}) => {
        if (event.detail === 0) stepBy(direction)
      },
    },
  }
}

export interface NumericRangeDecrementProps extends StepButtonProps {
  /** Shows a trash icon instead of the minus, for lists where stepping below the minimum removes the item. */
  removeItem?: boolean
}

/** Lowers the value by one step. Disabled at `min`, when read-only and when the range is disabled. */
export function NumericRangeDecrement({
  removeItem = false,
  className,
  onClick,
  ...props
}: NumericRangeDecrementProps) {
  const {isDisabled, handlers} = useStepButton(-1)

  return (
    <button
      type="button"
      {...props}
      {...handlers}
      onClick={(event) => {
        handlers.onClick(event)
        onClick?.(event)
      }}
      disabled={isDisabled}
      className={cn(stepButtonClassName, className)}
    >
      <Icon color="current" name={removeItem ? 'trash' : 'minus'} size="medium" />
    </button>
  )
}

/** Raises the value by one step. Disabled at `max`, when read-only and when the range is disabled. */
export function NumericRangeIncrement({className, onClick, ...props}: StepButtonProps) {
  const {isDisabled, handlers} = useStepButton(1)

  return (
    <button
      type="button"
      {...props}
      {...handlers}
      onClick={(event) => {
        handlers.onClick(event)
        onClick?.(event)
      }}
      disabled={isDisabled}
      className={cn(stepButtonClassName, className)}
    >
      <Icon color="current" name="plus" size="medium" />
    </button>
  )
}

export interface NumericRangeValueProps extends HTMLAttributes<HTMLSpanElement> {
  /** Replaces the value, e.g. a placeholder dash while the field is unavailable. */
  children?: ReactNode
}

/** The number between the buttons; shows `value` unless it gets children. */
export function NumericRangeValue({children, className, ...props}: NumericRangeValueProps) {
  const {value, disabled, readOnly} = useNumericRangeContext('NumericRangeValue')

  return (
    <span
      {...props}
      className={cn(
        'min-w-0 flex-1 truncate px-4 text-center select-none',
        readOnly && 'text-text-secondary',
        disabled && 'text-text-disabled',
        className,
      )}
    >
      {children ?? value}
    </span>
  )
}
